/**
 * AccuracyAuditPanel — Iter 40.2 ingestion scorecard.
 *
 * Reads GET /api/upload_batches/{id}/accuracy_report and renders:
 *   - Per-field correction counts (sheet number / title / discipline / revision)
 *   - Per-field accuracy %
 *   - Common OCR mistakes (top examples per field)
 *
 * This is the surface the future real-world FDOT/FAA audit reports against.
 * Numbers are computed from PERSISTED corrections_log entries — no estimates,
 * no confidence scores. Actual human corrections only.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ChevronDown, ChevronRight, Target } from "lucide-react";

const FIELD_LABEL = {
  sheet_number: "Sheet number",
  sheet_title: "Title",
  discipline: "Discipline",
  revision: "Revision",
};

function pctTone(p) {
  if (p >= 98) return "#10b981";
  if (p >= 90) return "#f59e0b";
  return "#ef4444";
}

export default function AccuracyAuditPanel({ batchId, refreshTick }) {
  const [report, setReport] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshError, setRefreshError] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/upload_batches/${batchId}/accuracy_report`);
      setReport(data);
      setRefreshError(false);
    } catch (e) {
      // 2026-09-14 (upload-remediation: failed-refresh honesty) — a
      // failed fetch must never silently keep showing a PREVIOUS
      // successful report as if it were current. Surface it explicitly
      // instead of leaving `report` (and the UI) untouched.
      setRefreshError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [batchId, refreshTick]);

  // Render the wrapper immediately so [data-testid="accuracy-audit-panel"]
  // is selectable from the first paint, even while the report is still
  // fetching. Avoids a race in automation where the panel appeared only
  // after a 1.5s round-trip.
  if (!report) {
    return (
      <section
        className="mb-5 border border-[var(--fop-border)] rounded-md bg-[var(--fop-surface)] p-3"
        data-testid="accuracy-audit-panel"
        data-loading={refreshError ? "0" : "1"}
      >
        <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
          <Target size={12} />
          Ingestion Accuracy Scorecard
          {refreshError ? (
            <span className="text-red-400" data-testid="accuracy-audit-error">
              · failed to load — retry
            </span>
          ) : (
            <span className="text-[var(--fop-text-4)]">· loading…</span>
          )}
        </div>
      </section>
    );
  }
  const acc = report.overall_accuracy;
  const notVerified = acc === null || acc === undefined;
  const tone = notVerified ? "#8a94a6" : pctTone(acc);
  const fc = report.field_correction_events || {};
  const fa = report.field_accuracy || {};
  const fr = report.field_reviewed || {};
  const totals = report.totals || {};
  const coverage = report.review_coverage || {};

  return (
    <section
      className="mb-5 border border-[var(--fop-border)] rounded-md bg-[var(--fop-surface)]"
      data-testid="accuracy-audit-panel"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-3 gap-3"
        data-testid="accuracy-audit-toggle"
      >
        <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-[var(--fop-text-2)]">
          {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          <Target size={12} className="text-[var(--fop-text-3)]" />
          Ingestion Accuracy Scorecard
        </div>
        <div className="inline-flex items-center gap-3 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
          <span data-testid="accuracy-overall">
            Overall: <span style={{ color: tone }}>{notVerified ? "Not verified" : `${acc}%`}</span>
          </span>
          <span data-testid="accuracy-review-coverage">
            Reviewed: <span className="text-[var(--fop-text)]">{coverage.reviewed ?? 0}</span> / {(totals.total ?? 0) - (totals.failed ?? 0)}
          </span>
        </div>
      </button>

      {open && (
        <div className="px-3 pb-4 border-t border-[var(--fop-border)] pt-3">
          {/* Per-field grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            {Object.keys(FIELD_LABEL).map((f) => {
              const events = fc[f] ?? 0;
              const accF = fa[f];
              const fieldNotVerified = accF === null || accF === undefined;
              return (
                <div
                  key={f}
                  className="border border-[var(--fop-border)] rounded p-2 bg-[var(--fop-surface-2)]"
                  data-testid={`accuracy-field-${f}`}
                >
                  <div className="font-mono text-[9px] uppercase tracking-wider text-[var(--fop-text-3)]">{FIELD_LABEL[f]}</div>
                  <div
                    className="font-display text-lg mt-0.5"
                    style={{ color: fieldNotVerified ? "#8a94a6" : pctTone(accF) }}
                    data-testid={`accuracy-field-${f}-value`}
                  >
                    {fieldNotVerified ? "Not verified" : `${accF}%`}
                  </div>
                  <div className="font-mono text-[10px] text-[var(--fop-text-3)]">
                    {events} correction{events === 1 ? "" : "s"} · reviewed {fr[f] ?? 0}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Totals row */}
          <div className="flex flex-wrap items-center gap-3 mb-3 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
            <span>Total: <span className="text-[var(--fop-text)]">{report.page_count}</span></span>
            <span>Needs review: <span className="text-amber-400">{totals.needs_review ?? 0}</span></span>
            <span>Failed: <span className="text-red-400">{totals.failed ?? 0}</span></span>
            <span>Published: <span className="text-blue-400">{totals.published ?? 0}</span></span>
            <span>Discarded: <span className="text-[var(--fop-steel)]">{totals.discarded ?? 0}</span></span>
            <span>Uncorrected: <span className="text-emerald-400">{totals.uncorrected ?? 0}</span></span>
          </div>

          {/* Common OCR mistakes per field */}
          <div className="space-y-2">
            {Object.entries(report.common_mistakes || {}).map(([f, examples]) =>
              examples.length > 0 ? (
                <div key={f} data-testid={`accuracy-mistakes-${f}`}>
                  <div className="font-mono text-[9px] uppercase tracking-wider text-[var(--fop-text-3)] mb-1">
                    Top {FIELD_LABEL[f]} OCR mistakes ({examples.length} shown)
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {examples.slice(0, 8).map((ex, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono text-[10px] bg-[var(--fop-surface-2)] border border-[var(--fop-border)]"
                        title={`page ${ex.page}`}
                      >
                        <span className="text-red-400 line-through">{ex.ocr ?? "—"}</span>
                        <span className="text-[var(--fop-text-3)]">→</span>
                        <span className="text-emerald-400">{ex.corrected ?? "—"}</span>
                      </span>
                    ))}
                  </div>
                </div>
              ) : null,
            )}
          </div>

          {loading && (
            <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mt-2">
              Refreshing…
            </div>
          )}
          {refreshError && !loading && (
            <div
              className="text-[11px] font-mono uppercase tracking-wider text-red-400 mt-2"
              data-testid="accuracy-audit-stale-warning"
            >
              Last refresh failed — figures below are STALE (as of the previous successful load), not current.
            </div>
          )}
        </div>
      )}
    </section>
  );
}
